import { NgModule, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

/*
 * Platform and Environment providers/directives/pipes
 */
import { environment } from 'environments/environment';
import { ROUTES } from './app.routes';
/*
 * Modules
 */
import { TeacherModule } from './../teacher/teacher.module';
import { LearnerModule } from './../learner/learner.module';
/*
 * Components
 */
// App is our top level component
import { App } from './components/main/app.component';
import { RoleBasedRouting } from './components/main/roleBasedRouting.component';
/*
 * Services
 */
import { CustomErrorHandler } from './services/customErrorHandler.service';
import { CustomHttpInterceptor } from './services/customHttpInterceptor.service';
import { ApiGatewayHelperService } from './services/apigateway-helper.service';
import { AuthService } from './services/auth.service';
import { GigyaSessionService } from './services/gigya-session.service';
import { AuthenticatedRoutesGuard } from './services/authenticated-routes-guard.service';
import { CustomTranslationLoader } from './services/custom-translation-loader';
import { ExternalRoutesGuard } from './services/external-routes-guard.service';
import { LoaderService } from './services/loader.service';
import { CacheService } from './services/cache.service';
import { CustomBackendLoggerService } from './services/custom-backend-logger.service';
import { AppHelperService } from './services/app-helper.service';
import { isUserAuthentcatedRoutesGuard } from './services/isUserAuthentcated-routes-guard.service';

import cavClient from 'libs-cavalier-integration/frontend';
import { UserDataService } from './services/user-data.service';

// initialize cavalier client
cavClient.init('nlp');
cavClient.capturePageViewEvents();

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [ App ],
  declarations: [
    App,
    RoleBasedRouting
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    RouterModule.forRoot(ROUTES, {
    preloadingStrategy: PreloadAllModules
}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader, 
        useClass: CustomTranslationLoader
      }
    }),
    TeacherModule,
    LearnerModule
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    environment.ENV_PROVIDERS,
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true } ,
    AuthService,
    UserDataService,
    ApiGatewayHelperService,
    GigyaSessionService,
    CustomTranslationLoader,
    AuthenticatedRoutesGuard,
    LoaderService,
    CacheService,
    CustomBackendLoggerService,
    AppHelperService,
    isUserAuthentcatedRoutesGuard,
    ExternalRoutesGuard,
    provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
      cookieName: 'csrf-token',
      headerName: 'csrf-token',
    })),
    provideZoneChangeDetection()
  ]
})
export class AppModule {}
